import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import {
  Building2, Layers, CalendarRange, ShieldCheck, Check, ChevronRight, ChevronLeft,
  Plus, Trash2, Loader2, Sparkles, Wand2, ChevronsUpDown, X, Bookmark, Save, Euro,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fmtInt, fmtEur } from '@/lib/format';

const MOIS_ABBR = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
const STATUT_CFG = {
  ferme: { label: 'Ferme', help: 'Volume engagé / signé', color: '#059669' },
  provisoire: { label: 'Provisoire', help: 'Accord de principe', color: '#d97706' },
  non_confirme: { label: 'Non confirmé', help: 'Piste à confirmer', color: '#94a3b8' },
};
const STEPS = [
  { n: 1, label: 'Association', icon: Building2 },
  { n: 2, label: 'Canaux & zones', icon: Layers },
  { n: 3, label: 'Volumes', icon: CalendarRange },
  { n: 4, label: 'Fiabilité', icon: ShieldCheck },
];

const lineKey = () => `w-${Math.random().toString(36).slice(2, 9)}`;

// Courbes de saisonnalité : poids relatifs par mois (index 0 = janvier … 11 = décembre).
// Appliquées en mode « Total annuel » pour répartir le total selon une forme réaliste,
// tout en conservant EXACTEMENT le total (méthode du plus grand reste).
const CURVES = {
  EGALE: {
    label: 'Égale', help: 'Même volume chaque mois',
    weights: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  },
  PRINTEMPS: {
    label: 'Printemps fort', help: 'Pic mars→juin, creux estival (juil./août)',
    weights: [0.8, 0.9, 1.2, 1.35, 1.35, 1.15, 0.6, 0.5, 1.1, 1.2, 1.1, 0.85],
  },
  RENTREE: {
    label: 'Rentrée forte', help: 'Pic septembre→novembre',
    weights: [0.8, 0.8, 0.9, 0.95, 1.0, 0.9, 0.6, 0.6, 1.5, 1.6, 1.4, 0.95],
  },
  FIN_ANNEE: {
    label: "Fin d'année forte", help: 'Pic novembre→décembre (collecte de fin d\'année)',
    weights: [0.85, 0.85, 0.9, 0.9, 0.95, 0.9, 0.7, 0.65, 1.0, 1.15, 1.6, 1.75],
  },
};

// Répartit un total entier sur les mois actifs [start..end] selon des poids (saisonnalité).
// Méthode du plus grand reste : le total est strictement conservé.
function distributeWeighted(total, start, end, weights) {
  const months = {};
  const t = Math.round(Number(total) || 0);
  if (!t) return months;
  const active = [];
  let wsum = 0;
  for (let m = start; m <= end; m += 1) {
    const w = Math.max(0, Number(weights?.[m - 1] ?? 1));
    active.push({ m, w });
    wsum += w;
  }
  if (!active.length || wsum <= 0) return months;
  let assigned = 0;
  const parts = active.map((a) => {
    const exact = (t * a.w) / wsum;
    const base = Math.floor(exact);
    assigned += base;
    return { m: a.m, base, frac: exact - base, w: a.w };
  });
  let rem = t - assigned;
  parts.sort((x, y) => (y.frac - x.frac) || (y.w - x.w) || (x.m - y.m));
  for (let i = 0; i < parts.length && rem > 0; i += 1) { parts[i].base += 1; rem -= 1; }
  parts.forEach((p) => { if (p.base) months[String(p.m)] = p.base; });
  return months;
}

function lineMonths(line, active, curve = 'EGALE') {
  if (line.mode === 'custom') return line.months || {};
  if (line.mode === 'even') {
    const months = {};
    const v = Math.round(Number(line.value) || 0);
    if (v) for (let m = active.start; m <= active.end; m += 1) months[String(m)] = v;
    return months;
  }
  // annual : répartition pondérée selon la courbe de saisonnalité sélectionnée
  const weights = (CURVES[curve] || CURVES.EGALE).weights;
  return distributeWeighted(line.value, active.start, active.end, weights);
}
const lineTotal = (line, active, curve = 'EGALE') => Object.values(lineMonths(line, active, curve)).reduce((a, b) => a + (b || 0), 0);

export function ProgrammationWizard({ open, onClose, meta, year, associations, initialCode, onSaved }) {
  const [step, setStep] = useState(1);
  const [assoc, setAssoc] = useState(null);           // { code, name, is_prospect }
  const [creating, setCreating] = useState(false);
  const [npCode, setNpCode] = useState('');
  const [npName, setNpName] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [lines, setLines] = useState([]);
  const [active, setActive] = useState({ start: 1, end: 12 });
  const [globalStatut, setGlobalStatut] = useState('provisoire');
  const [curve, setCurve] = useState('EGALE');
  const [regionsLocal, setRegionsLocal] = useState(meta?.regions || []);
  const [newZone, setNewZone] = useState('');
  const [zonePop, setZonePop] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplApplyPop, setTplApplyPop] = useState(false);
  const [tplSavePop, setTplSavePop] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplScope, setTplScope] = useState('association');
  const [tplBusy, setTplBusy] = useState(false);
  const [prices, setPrices] = useState({}); // {canal: prix convenu} pour le chiffre d'affaires

  const canaux = meta?.canaux || [];
  const reset = useCallback(() => {
    setStep(1); setAssoc(null); setCreating(false); setNpCode(''); setNpName('');
    setLines([]); setActive({ start: 1, end: 12 }); setGlobalStatut('provisoire');
    setCurve('EGALE'); setPrices({});
  }, []);

  useEffect(() => { if (open) { reset(); setRegionsLocal(meta?.regions || []); } }, [open, meta, reset]);

  // Charge les modèles de canaux/zones disponibles (globaux + ceux de l'association).
  const loadTemplates = useCallback(async (code) => {
    try {
      const { data } = await api.get('/rex/programmation/templates', { params: { association_code: code } });
      setTemplates(data.templates || []);
    } catch (e) { /* silencieux */ }
  }, []);

  // Charge les tarifs par canal existants de l'association.
  const loadPrices = useCallback(async (code) => {
    try {
      const { data } = await api.get('/rex/programmation/prices', { params: { association_code: code, year } });
      setPrices(data.prices || {});
    } catch (e) { setPrices({}); }
  }, [year]);

  // Pré-charge le plan existant de l'association (pour éditer sans perte) + préselection.
  const loadExisting = useCallback(async (code, name, isProspect) => {
    setAssoc({ code, name, is_prospect: isProspect });
    setLoadingPlan(true);
    loadTemplates(code);
    loadPrices(code);
    try {
      const { data } = await api.get('/rex/programmation/plan', { params: { association_code: code, year } });
      const existing = (data?.plan?.lines || []).map((l) => ({
        key: lineKey(), canal: l.canal || '', region: l.region || '',
        mode: 'custom', value: '', months: l.months || {}, statut: l.statut || 'provisoire',
      }));
      setLines(existing.length ? existing : [{ key: lineKey(), canal: '', region: '', mode: 'annual', value: '', months: {}, statut: 'provisoire' }]);
    } catch (e) {
      setLines([{ key: lineKey(), canal: '', region: '', mode: 'annual', value: '', months: {}, statut: 'provisoire' }]);
    }
    setLoadingPlan(false);
    setStep(2);
  }, [year, loadTemplates, loadPrices]);

  // Lancement depuis une ligne prospect : préselection directe.
  useEffect(() => {
    if (open && initialCode) {
      const a = (associations || []).find((x) => x.code === initialCode);
      if (a) loadExisting(a.code, a.name, a.is_prospect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCode]);

  const grand = useMemo(() => lines.reduce((a, l) => a + lineTotal(l, active, curve), 0), [lines, active, curve]);

  // Volume total par canal (agrégé toutes zones) — base du chiffre d'affaires prévisionnel.
  const caByCanal = useMemo(() => {
    const totals = {};
    lines.forEach((l) => {
      if (!l.canal) return;
      const t = lineTotal(l, active, curve);
      if (t > 0) totals[l.canal] = (totals[l.canal] || 0) + t;
    });
    return totals;
  }, [lines, active, curve]);
  const priceNum = useCallback((c) => Number(String(prices[c] ?? '').replace(',', '.')) || 0, [prices]);
  const caTotal = useMemo(
    () => Object.entries(caByCanal).reduce((a, [c, v]) => a + v * priceNum(c), 0),
    [caByCanal, priceNum],
  );

  const updLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = (preset = {}) => setLines((ls) => [...ls, { key: lineKey(), canal: '', region: '', mode: 'annual', value: '', months: {}, statut: globalStatut, ...preset }]);
  const delLine = (key) => setLines((ls) => ls.filter((l) => l.key !== key));
  const addAllCanaux = () => setLines((ls) => {
    const have = new Set(ls.map((l) => l.canal));
    const toAdd = canaux.filter((c) => !have.has(c)).map((c) => ({ key: lineKey(), canal: c, region: '', mode: 'annual', value: '', months: {}, statut: globalStatut }));
    return [...ls, ...toAdd];
  });

  // Applique un modèle : crée les lignes (canal × zone) absentes, volumes à saisir au Step 3.
  const applyTemplate = (tpl) => {
    setLines((ls) => {
      const have = new Set(ls.filter((l) => l.canal).map((l) => `${l.canal}|${l.region || ''}`));
      const toAdd = (tpl.lines || [])
        .filter((t) => t.canal && !have.has(`${t.canal}|${t.region || ''}`))
        .map((t) => ({ key: lineKey(), canal: t.canal, region: t.region || '', mode: 'annual', value: '', months: {}, statut: t.statut || globalStatut }));
      // Retire d'éventuelles lignes vides initiales si le modèle apporte du contenu
      const base = toAdd.length ? ls.filter((l) => l.canal) : ls;
      return [...base, ...toAdd];
    });
    setTplApplyPop(false);
    toast.success(`Modèle « ${tpl.name} » appliqué`);
  };

  const saveTemplate = async () => {
    const name = (tplName || '').trim();
    if (!name) { toast.error('Nom du modèle requis'); return; }
    const linesMeta = lines.filter((l) => l.canal).map((l) => ({ canal: l.canal, region: l.region || '', statut: l.statut || globalStatut }));
    if (!linesMeta.length) { toast.error('Ajoutez au moins un canal avant d\'enregistrer un modèle'); return; }
    setTplBusy(true);
    try {
      await api.post('/rex/programmation/templates', {
        name, scope: tplScope, association_code: assoc?.code, lines: linesMeta,
      });
      toast.success(`Modèle « ${name} » enregistré`);
      setTplName(''); setTplSavePop(false);
      loadTemplates(assoc?.code);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement du modèle impossible'); }
    setTplBusy(false);
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/rex/programmation/templates/${id}`);
      setTemplates((ts) => ts.filter((t) => t.id !== id));
      toast.success('Modèle supprimé');
    } catch (e) { toast.error('Suppression impossible'); }
  };

  const createProspect = async () => {
    const code = (npCode || '').trim().toUpperCase();
    const name = (npName || '').trim();
    if (!code || !name) { toast.error('Code et nom requis'); return; }
    try {
      await api.post('/rex/programmation/associations', { code, name });
      toast.success(`Association « ${name} » créée`);
      await loadExisting(code, name, true);
      setCreating(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Création impossible'); }
  };

  const addZone = async () => {
    const name = (newZone || '').trim();
    if (!name) return;
    try {
      const { data } = await api.post('/rex/programmation/regions', { name });
      setRegionsLocal(data.regions || []);
      toast.success(`Zone « ${name} » ajoutée`);
      setNewZone(''); setZonePop(false);
    } catch (e) { toast.error('Ajout de zone impossible'); }
  };

  const applyToAll = (mode, value) => setLines((ls) => ls.map((l) => ({ ...l, mode, value })));

  const save = async () => {
    const valid = lines.filter((l) => l.canal && lineTotal(l, active, curve) > 0);
    if (!valid.length) { toast.error('Ajoutez au moins une ligne avec un volume'); return; }
    setSaving(true);
    try {
      await api.put('/rex/programmation/plan', {
        association_code: assoc.code, year,
        lines: valid.map((l) => ({
          canal: l.canal, region: l.region, months: lineMonths(l, active, curve),
          statut: l.statut || globalStatut,
        })),
        forecast_overrides: {},
      });
      // Enregistre les tarifs par canal saisis (base du chiffre d'affaires), si présents.
      const usedCanaux = Array.from(new Set(valid.map((l) => l.canal)));
      const priceMap = {};
      usedCanaux.forEach((c) => { priceMap[c] = Number(String(prices[c] ?? '').replace(',', '.')) || 0; });
      if (Object.values(priceMap).some((p) => p > 0)) {
        try { await api.put('/rex/programmation/prices', { association_code: assoc.code, year, prices: priceMap }); } catch (e) { /* non bloquant */ }
      }
      toast.success(`Programmation ${year} enregistrée pour ${assoc.name} · ${fmtInt(grand)} bulletins`);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setSaving(false);
  };

  const canNext = (step === 1 && assoc)
    || (step === 2 && lines.some((l) => l.canal))
    || (step === 3 && grand > 0)
    || step === 4;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-[95vw] overflow-y-auto sm:max-w-[920px]" data-testid="rex-prog-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />
            Assistant de programmation {year}
            {assoc && <span className="text-sm font-normal text-muted-foreground">— {assoc.name}</span>}
          </DialogTitle>
          <DialogDescription>Configurez les volumes prévisionnels en quelques clics : canaux, zones, puis répartition mensuelle automatique.</DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1" data-testid="rex-prog-wizard-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = step > s.n; const cur = step === s.n;
            return (
              <React.Fragment key={s.n}>
                <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${cur ? 'text-white' : done ? 'text-foreground' : 'text-muted-foreground'}`}
                  style={cur ? { background: 'var(--tenant-accent-500)' } : done ? { background: 'var(--tenant-accent-100)' } : {}}>
                  {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="h-px w-4 bg-border sm:w-6" />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="min-h-[320px] py-2">
          {/* STEP 1 — Association */}
          {step === 1 && (
            <div className="space-y-4" data-testid="rex-prog-wizard-step1">
              {!creating ? (
                <>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">Choisir une association</label>
                    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between" data-testid="rex-prog-wizard-assoc-picker">
                          {assoc ? `${assoc.name} (${assoc.code})` : 'Sélectionner…'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Rechercher une association…" />
                          <CommandList>
                            <CommandEmpty>Aucune association.</CommandEmpty>
                            <CommandGroup>
                              {(associations || []).map((a) => (
                                <CommandItem key={a.code} value={`${a.name} ${a.code}`}
                                  onSelect={() => { setPickerOpen(false); loadExisting(a.code, a.name, a.is_prospect); }}
                                  data-testid={`rex-prog-wizard-assoc-${a.code}`}>
                                  <Check className={`mr-2 h-4 w-4 ${assoc?.code === a.code ? 'opacity-100' : 'opacity-0'}`} />
                                  <span className="flex-1">{a.name}</span>
                                  {a.is_prospect && <Badge variant="outline" className="ml-2 h-5 px-1.5 text-[10px]">prospect</Badge>}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="h-px flex-1 bg-border" />ou<span className="h-px flex-1 bg-border" />
                  </div>
                  <Button variant="outline" onClick={() => setCreating(true)} className="w-full" data-testid="rex-prog-wizard-new-prospect">
                    <Plus className="mr-1.5 h-4 w-4" />Créer une nouvelle association (prospect)
                  </Button>
                </>
              ) : (
                <div className="space-y-3 rounded-lg border bg-muted/30 p-3" data-testid="rex-prog-wizard-create">
                  <p className="text-sm font-medium">Nouvelle association (prospect)</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-1">
                      <label className="mb-1 block text-xs text-muted-foreground">Code</label>
                      <Input value={npCode} onChange={(e) => setNpCode(e.target.value.toUpperCase())} placeholder="ex. GP" data-testid="rex-prog-wizard-np-code" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs text-muted-foreground">Nom</label>
                      <Input value={npName} onChange={(e) => setNpName(e.target.value)} placeholder="ex. Greenpeace" data-testid="rex-prog-wizard-np-name" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Annuler</Button>
                    <Button size="sm" onClick={createProspect} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid="rex-prog-wizard-create-confirm">
                      <Check className="mr-1.5 h-4 w-4" />Créer et continuer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 — Canaux & zones */}
          {step === 2 && (
            <div className="space-y-3" data-testid="rex-prog-wizard-step2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">Ajoutez une ligne par <b>canal</b> (et par <b>zone</b> si besoin). Une ligne sans zone = national.</p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={addAllCanaux} data-testid="rex-prog-wizard-add-all"><Layers className="mr-1.5 h-3.5 w-3.5" />Tous les canaux</Button>
                  {/* Modèles : appliquer un modèle de canaux/zones enregistré */}
                  <Popover open={tplApplyPop} onOpenChange={setTplApplyPop}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="rex-prog-wizard-tpl-apply">
                        <Bookmark className="mr-1.5 h-3.5 w-3.5" />Modèles{templates.length ? ` (${templates.length})` : ''}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-2" align="end">
                      <p className="mb-1.5 px-1 text-xs font-medium">Appliquer un modèle de canaux / zones</p>
                      {templates.length === 0 ? (
                        <p className="px-1 py-3 text-xs text-muted-foreground">Aucun modèle enregistré. Configurez vos canaux puis « Enregistrer modèle ».</p>
                      ) : (
                        <div className="max-h-64 space-y-1 overflow-y-auto">
                          {templates.map((t) => (
                            <div key={t.id} className="flex items-center gap-1.5 rounded-md border bg-card px-2 py-1.5" data-testid={`rex-prog-wizard-tpl-${t.id}`}>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="truncate text-xs font-medium">{t.name}</span>
                                  <Badge variant="outline" className="h-4 px-1 text-[9px]">{t.scope === 'association' ? 'asso' : 'global'}</Badge>
                                </div>
                                <div className="truncate text-[10px] text-muted-foreground">{(t.lines || []).map((x) => x.canal + (x.region ? ` · ${x.region}` : '')).join(' • ')}</div>
                              </div>
                              <Button size="sm" className="h-7 px-2 text-[11px]" onClick={() => applyTemplate(t)} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid={`rex-prog-wizard-tpl-apply-${t.id}`}>Appliquer</Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => deleteTemplate(t.id)} data-testid={`rex-prog-wizard-tpl-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  {/* Enregistrer les canaux/zones actuels comme modèle réutilisable */}
                  <Popover open={tplSavePop} onOpenChange={setTplSavePop}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="rex-prog-wizard-tpl-save-open"><Save className="mr-1.5 h-3.5 w-3.5" />Enregistrer modèle</Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-3" align="end">
                      <p className="mb-2 text-xs font-medium">Enregistrer les canaux/zones comme modèle</p>
                      <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="ex. Mix P2P + Sites Privés" className="h-8 text-xs" onKeyDown={(e) => e.key === 'Enter' && saveTemplate()} data-testid="rex-prog-wizard-tpl-name" />
                      <div className="mt-2 flex gap-1.5">
                        {[['association', 'Cette association'], ['global', 'Toutes']].map(([k, lbl]) => (
                          <button key={k} type="button" onClick={() => setTplScope(k)}
                            className={`flex-1 rounded-md border px-2 py-1 text-[11px] transition-colors ${tplScope === k ? 'border-transparent text-white' : 'bg-card hover:bg-muted/60'}`}
                            style={tplScope === k ? { background: 'var(--tenant-accent-500)' } : {}} data-testid={`rex-prog-wizard-tpl-scope-${k}`}>{lbl}</button>
                        ))}
                      </div>
                      <Button size="sm" className="mt-2 h-8 w-full text-xs" onClick={saveTemplate} disabled={tplBusy} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid="rex-prog-wizard-tpl-save">
                        {tplBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}Enregistrer le modèle
                      </Button>
                    </PopoverContent>
                  </Popover>
                  <Popover open={zonePop} onOpenChange={setZonePop}>
                    <PopoverTrigger asChild><Button variant="outline" size="sm" data-testid="rex-prog-wizard-add-zone"><Plus className="mr-1.5 h-3.5 w-3.5" />Nouvelle zone</Button></PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="end">
                      <p className="mb-1.5 text-xs font-medium">Créer une zone / région</p>
                      <div className="flex gap-1.5">
                        <Input value={newZone} onChange={(e) => setNewZone(e.target.value)} placeholder="ex. Occitanie" className="h-8 text-xs" onKeyDown={(e) => e.key === 'Enter' && addZone()} />
                        <Button size="sm" className="h-8" onClick={addZone}><Check className="h-4 w-4" /></Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              {loadingPlan ? (
                <div className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
              ) : (
                <div className="space-y-2">
                  {lines.map((l) => (
                    <div key={l.key} className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-2" data-testid={`rex-prog-wizard-line-${l.key}`}>
                      <Select value={l.canal || undefined} onValueChange={(v) => updLine(l.key, { canal: v })}>
                        <SelectTrigger className="h-9 w-[200px]" data-testid={`rex-prog-wizard-canal-${l.key}`}><SelectValue placeholder="Canal…" /></SelectTrigger>
                        <SelectContent>{canaux.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={l.region || '__none__'} onValueChange={(v) => updLine(l.key, { region: v === '__none__' ? '' : v })}>
                        <SelectTrigger className="h-9 w-[200px]" data-testid={`rex-prog-wizard-zone-${l.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Zone nationale (toutes)</SelectItem>
                          {regionsLocal.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="ml-auto h-8 w-8 text-rose-500" onClick={() => delLine(l.key)} data-testid={`rex-prog-wizard-del-${l.key}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="w-full gap-1 border-dashed text-xs text-muted-foreground hover:text-foreground" onClick={() => addLine()} data-testid="rex-prog-wizard-add-line"><Plus className="h-3.5 w-3.5" style={{ color: 'var(--tenant-accent-600)' }} />Ajouter une ligne</Button>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — Volumes */}
          {step === 3 && (
            <div className="space-y-4" data-testid="rex-prog-wizard-step3">
              <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 p-3">
                <CalendarRange className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Période active</span>
                <Select value={String(active.start)} onValueChange={(v) => setActive((a) => ({ ...a, start: Math.min(Number(v), a.end) }))}>
                  <SelectTrigger className="h-8 w-[100px]" data-testid="rex-prog-wizard-active-start"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOIS_ABBR.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">→</span>
                <Select value={String(active.end)} onValueChange={(v) => setActive((a) => ({ ...a, end: Math.max(Number(v), a.start) }))}>
                  <SelectTrigger className="h-8 w-[100px]" data-testid="rex-prog-wizard-active-end"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOIS_ABBR.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <span className="ml-auto text-xs text-muted-foreground">La répartition automatique s'étale sur ces mois.</span>
              </div>

              {/* Saisonnalité — forme de répartition du « Total annuel » */}
              <div className="rounded-lg border bg-muted/30 p-3" data-testid="rex-prog-wizard-season">
                <div className="flex flex-wrap items-center gap-3">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Saisonnalité</span>
                  <Select value={curve} onValueChange={setCurve}>
                    <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="rex-prog-wizard-curve"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CURVES).map(([k, c]) => (
                        <SelectItem key={k} value={k} data-testid={`rex-prog-wizard-curve-${k}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-muted-foreground">{CURVES[curve]?.help}</span>
                  <span className="ml-auto text-[11px] text-muted-foreground">S'applique aux lignes en « Total annuel »</span>
                </div>
                {/* Aperçu visuel de la courbe (mois actifs) */}
                <div className="mt-2 grid grid-cols-12 gap-1" data-testid="rex-prog-wizard-curve-preview">
                  {MOIS_ABBR.map((mm, i) => {
                    const m = i + 1;
                    const act = m >= active.start && m <= active.end;
                    const weights = (CURVES[curve] || CURVES.EGALE).weights;
                    const maxW = Math.max(
                      ...weights.map((ww, j) => ((j + 1) >= active.start && (j + 1) <= active.end ? ww : 0)),
                      0.0001,
                    );
                    const h = act ? Math.max(8, Math.round((weights[i] / maxW) * 34)) : 3;
                    return (
                      <div key={m} className="flex flex-col items-center justify-end">
                        <div className="flex h-9 w-full items-end">
                          <div
                            className="w-full rounded-sm transition-[height] duration-200"
                            style={{ height: h, background: act ? 'var(--tenant-accent-500)' : 'var(--border)', opacity: act ? 1 : 0.5 }}
                          />
                        </div>
                        <div className="mt-0.5 text-[9px] text-muted-foreground">{mm}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                {lines.filter((l) => l.canal).map((l) => {
                  const months = lineMonths(l, active, curve);
                  return (
                    <div key={l.key} className="rounded-lg border bg-card p-3" data-testid={`rex-prog-wizard-vol-${l.key}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="h-6">{l.canal}</Badge>
                        <span className="text-xs text-muted-foreground">{l.region || 'National'}</span>
                        <div className="ml-auto flex items-center gap-2">
                          <Select value={l.mode} onValueChange={(v) => updLine(l.key, { mode: v })}>
                            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`rex-prog-wizard-mode-${l.key}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="annual">Total annuel</SelectItem>
                              <SelectItem value="even">Même valeur / mois</SelectItem>
                              <SelectItem value="custom">Personnalisé</SelectItem>
                            </SelectContent>
                          </Select>
                          {l.mode !== 'custom' && (
                            <Input type="number" value={l.value} onChange={(e) => updLine(l.key, { value: e.target.value })}
                              placeholder={l.mode === 'annual' ? 'Total année' : '/ mois'} className="h-8 w-[120px] text-center text-xs" data-testid={`rex-prog-wizard-val-${l.key}`} />
                          )}
                          <span className="min-w-[70px] text-right text-sm font-semibold tabular-nums" style={{ color: 'var(--tenant-accent-600)' }} data-testid={`rex-prog-wizard-total-${l.key}`}>{fmtInt(lineTotal(l, active, curve))}</span>
                        </div>
                      </div>
                      {/* Aperçu / édition mensuelle */}
                      <div className="mt-2 grid grid-cols-6 gap-1 sm:grid-cols-12">
                        {MOIS_ABBR.map((mm, i) => {
                          const m = i + 1;
                          const val = months[String(m)] || 0;
                          const act = m >= active.start && m <= active.end;
                          return l.mode === 'custom' ? (
                            <div key={m} className="text-center">
                              <div className="mb-0.5 text-[9px] text-muted-foreground">{mm}</div>
                              <Input type="number" value={(l.months || {})[String(m)] ?? ''} onChange={(e) => {
                                const months2 = { ...(l.months || {}) };
                                if (e.target.value === '') delete months2[String(m)]; else months2[String(m)] = Math.round(Number(e.target.value)) || 0;
                                updLine(l.key, { months: months2 });
                              }} className="h-7 px-1 text-center text-[11px] tabular-nums" />
                            </div>
                          ) : (
                            <div key={m} className={`rounded px-1 py-1 text-center text-[11px] tabular-nums ${act ? 'bg-muted/60' : 'bg-muted/20 text-muted-foreground'}`}>
                              <div className="text-[9px] text-muted-foreground">{mm}</div>
                              {val || '·'}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2">
                <span className="text-sm font-medium">Total programmé {year}</span>
                <span className="text-base font-semibold tabular-nums" style={{ color: 'var(--tenant-accent-600)' }} data-testid="rex-prog-wizard-grand">{fmtInt(grand)} bulletins</span>
              </div>
            </div>
          )}

          {/* STEP 4 — Fiabilité + récap */}
          {step === 4 && (
            <div className="space-y-4" data-testid="rex-prog-wizard-step4">
              <div>
                <p className="mb-2 text-sm font-medium">Fiabilité par défaut</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUT_CFG).map(([k, c]) => (
                    <button key={k} type="button" onClick={() => { setGlobalStatut(k); setLines((ls) => ls.map((l) => ({ ...l, statut: k }))); }}
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${globalStatut === k ? 'border-transparent text-white' : 'bg-card hover:bg-muted/60'}`}
                      style={globalStatut === k ? { background: c.color } : {}} data-testid={`rex-prog-wizard-statut-${k}`}>
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: globalStatut === k ? 'rgba(255,255,255,.9)' : c.color }} />
                      <span className="font-medium">{c.label}</span>
                      <span className={`text-xs ${globalStatut === k ? 'text-white/80' : 'text-muted-foreground'}`}>· {c.help}</span>
                    </button>
                  ))}
                </div>
              </div>
              {Object.keys(caByCanal).length > 0 && (
                <div data-testid="rex-prog-wizard-prices">
                  <div className="mb-1.5 flex items-center gap-2">
                    <Euro className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />
                    <p className="text-sm font-medium">Tarifs par canal <span className="font-normal text-muted-foreground">· chiffre d'affaires prévisionnel (optionnel)</span></p>
                  </div>
                  <p className="mb-2 text-xs text-muted-foreground">Prix convenu par bulletin, par canal, pour <b>{assoc?.name}</b>. Il s'applique à toutes les zones et se partage avec la vue « Chiffre d'affaires » de la matrice.</p>
                  <div className="overflow-hidden rounded-lg border">
                    <div className="grid grid-cols-[1fr_5rem_7rem_8rem] items-center gap-2 border-b bg-muted/40 px-3 py-1.5 text-[11px] font-medium text-muted-foreground">
                      <span>Canal</span>
                      <span className="text-right">Volume</span>
                      <span className="text-right">Prix unitaire</span>
                      <span className="text-right">CA prévisionnel</span>
                    </div>
                    <div className="divide-y">
                      {Object.entries(caByCanal).map(([c, vol]) => (
                        <div key={c} className="grid grid-cols-[1fr_5rem_7rem_8rem] items-center gap-2 px-3 py-1.5" data-testid={`rex-prog-wizard-price-row-${c}`}>
                          <Badge variant="outline" className="h-6 w-fit">{c}</Badge>
                          <span className="text-right text-xs tabular-nums text-muted-foreground">{fmtInt(vol)}</span>
                          <div className="relative">
                            <Input
                              value={prices[c] ?? ''}
                              onChange={(e) => setPrices((p) => ({ ...p, [c]: e.target.value }))}
                              inputMode="decimal"
                              placeholder="0,00"
                              className="h-8 pr-6 text-right text-xs tabular-nums"
                              data-testid={`rex-prog-wizard-price-${c}`}
                            />
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                          </div>
                          <span className="text-right text-xs font-semibold tabular-nums" style={priceNum(c) > 0 ? { color: 'var(--tenant-accent-600)' } : {}} data-testid={`rex-prog-wizard-price-ca-${c}`}>
                            {priceNum(c) > 0 ? fmtEur(vol * priceNum(c), 0) : <span className="text-muted-foreground opacity-40">—</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                    {caTotal > 0 && (
                      <div className="grid grid-cols-[1fr_8rem] items-center gap-2 border-t bg-muted/30 px-3 py-1.5 text-xs font-semibold" data-testid="rex-prog-wizard-ca-total">
                        <span>Chiffre d'affaires prévisionnel {year}</span>
                        <span className="text-right tabular-nums" style={{ color: 'var(--tenant-accent-600)' }}>{fmtEur(caTotal, 0)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="rounded-lg border">
                <div className="border-b bg-muted/40 px-3 py-2 text-sm font-medium">Récapitulatif</div>
                <div className="divide-y">
                  {lines.filter((l) => l.canal && lineTotal(l, active, curve) > 0).map((l) => (
                    <div key={l.key} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`rex-prog-wizard-recap-${l.key}`}>
                      <Badge variant="outline" className="h-6">{l.canal}</Badge>
                      <span className="text-muted-foreground">{l.region || 'National'}</span>
                      <span className="ml-auto font-semibold tabular-nums">{fmtInt(lineTotal(l, active, curve))}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 bg-muted/30 px-3 py-2 text-sm font-semibold">
                    <span>Total {year}</span>
                    <span className="ml-auto tabular-nums" style={{ color: 'var(--tenant-accent-600)' }}>{fmtInt(grand)} bulletins</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <Button variant="ghost" onClick={step === 1 ? onClose : () => setStep((s) => s - 1)} data-testid="rex-prog-wizard-back">
            {step === 1 ? <><X className="mr-1.5 h-4 w-4" />Annuler</> : <><ChevronLeft className="mr-1.5 h-4 w-4" />Retour</>}
          </Button>
          {step < 4 ? (
            <Button disabled={!canNext} onClick={() => setStep((s) => s + 1)} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid="rex-prog-wizard-next">
              Continuer<ChevronRight className="ml-1.5 h-4 w-4" />
            </Button>
          ) : (
            <Button disabled={saving || grand <= 0} onClick={save} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid="rex-prog-wizard-save">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}Enregistrer la programmation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
